import { useState, type FormEvent } from 'react';
import {
  Briefcase,
  Eye,
  SpinnerGap,
  Envelope,
  ShieldCheck,
  UserPlus,
  UsersThree,
  X,
} from '@phosphor-icons/react';
import { useInviteEmployee, type InviteRole } from '@/lib/employees/useInviteEmployee';
import { cn } from '@/lib/utils/cn';
import type { ReactNode } from 'react';

const ROLES: { value: InviteRole; label: string; hint: string; icon: ReactNode }[] = [
  {
    value: 'admin',
    label: 'Admin',
    hint: 'Full access — manage teammates, candidates, licenses, and billing.',
    icon: <ShieldCheck className="h-3.5 w-3.5" />,
  },
  {
    value: 'team_lead',
    label: 'Team Lead',
    hint: 'Manage their team, add candidates, and issue NeuraX licenses.',
    icon: <UsersThree className="h-3.5 w-3.5" />,
  },
  {
    value: 'recruiter',
    label: 'Recruiter',
    hint: 'Add their own candidates and issue NeuraX licenses.',
    icon: <Briefcase className="h-3.5 w-3.5" />,
  },
  {
    value: 'viewer',
    label: 'Viewer',
    hint: 'Read-only access to the portal.',
    icon: <Eye className="h-3.5 w-3.5" />,
  },
];

export function InviteEmployeeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const invite = useInviteEmployee();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<InviteRole>('recruiter');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setEmail('');
    setFullName('');
    setRole('recruiter');
    setError(null);
    setSuccess(null);
  }

  function close() {
    if (invite.isPending) return;
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      await invite.mutateAsync({ email, full_name: fullName, role });
      setSuccess(`Invitation sent to ${email}.`);
      setEmail('');
      setFullName('');
      setRole('recruiter');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Invite teammate"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-neurax-midnight p-6 shadow-glow">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-electric/50 via-neurax-plasma/50 to-transparent" />

        <button
          type="button"
          onClick={close}
          disabled={invite.isPending}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/30 to-neurax-plasma/30 text-neurax-electric">
            <UserPlus className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-white">Invite teammate</h2>
            <p className="text-xs text-muted-foreground">
              They'll get a magic link to set their password and join the workspace.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Full name
            </label>
            <input
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              disabled={invite.isPending}
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30 disabled:opacity-50"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Work email
            </label>
            <div className="relative">
              <Envelope className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@company.com"
                disabled={invite.isPending}
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30 disabled:opacity-50"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Role
            </label>
            <div className="grid gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  disabled={invite.isPending}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition disabled:opacity-50',
                    role === r.value
                      ? 'border-neurax-electric/40 bg-neurax-electric/5'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                  )}
                >
                  <div
                    className={cn(
                      'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg',
                      role === r.value
                        ? 'bg-neurax-electric/15 text-neurax-electric'
                        : 'bg-white/[0.04] text-muted-foreground'
                    )}
                  >
                    {r.icon}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white">{r.label}</p>
                    <p className="text-[11px] text-muted-foreground">{r.hint}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              role="status"
              className="rounded-lg border border-neurax-mint/30 bg-neurax-mint/10 px-3 py-2 text-xs text-neurax-mint"
            >
              {success}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={close}
              disabled={invite.isPending}
              className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:text-white disabled:opacity-50"
            >
              {success ? 'Done' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={invite.isPending || !email || !fullName}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:opacity-60"
            >
              {invite.isPending ? (
                <>
                  <SpinnerGap className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" />
                  Send invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
